export interface ReportRequest {
  /** Nyckeln ur klasslänkens #key=. Utan den går det inte att rapportera. */
  token: string;
  /** ISO-datum, t.ex. "2026-09-23". */
  date: string;
  /** Längden på Breaket. */
  minutesPerBreak: number;
  /** Hur många elever som faktiskt var med. */
  participatingStudents: number;
}

export interface ReportResponse {
  /** Klassens totala antal Breaks efter rapporten. Null om domänen inte räknade om. */
  totalForClass: number | null;
}

/** Fel som gick att tolka ur API-svaret, med den text som ska visas för användaren. */
export class ReportError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "ReportError";
    this.status = status;
  }
}

const REPORT_URL = "/breaks/api/reports";

/**
 * Prövar att nyckeln hör till klassen på sidan. Det är också det som bekräftar anmälan,
 * så anropet görs först när en människa öppnat länken i en webbläsare.
 * True vid giltig nyckel, false vid ogiltig. Kastar ReportError vid övriga fel.
 */
export async function verifyClassLink(
  slug: string,
  token: string,
): Promise<boolean> {
  let response: Response;

  try {
    response = await fetch(`${REPORT_URL}/verify`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ slug, token }),
    });
  } catch {
    throw new ReportError(
      "Klasslänken kunde inte kontrolleras. Kontrollera din uppkoppling.",
      0,
    );
  }

  if (response.status === 401) return false;

  if (!response.ok) {
    throw new ReportError(await readError(response), response.status);
  }

  return true;
}

export async function reportBreaks(
  request: ReportRequest,
  signal?: AbortSignal,
): Promise<ReportResponse> {
  let response: Response;

  try {
    response = await fetch(REPORT_URL, {
      method: "POST",
      signal,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(request),
    });
  } catch {
    // Nätverksfel ser annorlunda ut än ett felsvar och måste fångas separat.
    throw new ReportError(
      "Rapporten kunde inte skickas. Kontrollera din uppkoppling.",
      0,
    );
  }

  if (!response.ok) {
    throw new ReportError(await readError(response), response.status);
  }

  return (await response.json()) as ReportResponse;
}

/**
 * Plockar ut felmeddelandet ur svaret. Controllern svarar med { error } medan
 * modellvalidering ger ProblemDetails, så båda formerna hanteras.
 */
async function readError(response: Response): Promise<string> {
  try {
    const body = await response.json();

    if (typeof body?.error === "string") return body.error;
    if (typeof body?.title === "string") return body.title;
  } catch {
    // Svaret var inte JSON. Faller tillbaka på statuskoden nedan.
  }

  return response.status === 401
    ? "Klasslänken gäller inte längre. Be om en ny länk via anmälan på startsidan."
    : "Rapporten kunde inte sparas. Försök igen senare.";
}
